import React from 'react';
import styled from 'styled-components';
// import { CopyIcon, ExternalLinkIcon, InfoIcon, NevadaSealPlaceholder } from '../Icons';
import { OrganizationCardProps } from '../../../types/types';
import NewCopyIcon from '../../../assets/icons/NewCopyIcon';
import InfoIcon from '../../../assets/icons/InfoIcon';
import Avatar from '../../../components/Avatar/Avatar';
import ViewIcon from '../../../assets/icons/ViewIcon';

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  transition: box-shadow 0.2s ease;
  height: fit-content;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
`;

const LogoContainer = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  border: 3px solid #f9fafb;
`;

const Logo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeaderContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const OrganizationName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const IdBadge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background-color: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #e5e7eb;
    border-color: #9ca3af;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const PrimaryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 12px;
  background-color: #009828;
  color: white;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;
  margin-left: auto;
`;

const SourceBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  background-color: #FEF3C7;
  border-radius: 6px;
  font-size: 12px;
  color: #92400E;
  width: fit-content;
  
  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

const DetailsSection = styled.div`
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  flex-shrink: 0;
  min-width: 100px;
`;

const DetailValue = styled.span`
  font-size: 13px;
  color: #374151;
  word-break: break-word;
`;

const ViewDetailsButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f9fafb;
    border-color: #d1d5db;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const OrganizationCard: React.FC<OrganizationCardProps> = ({ 
  OrganizationInfo,
  onViewDetails 
}) => {
  const data = OrganizationInfo as OrganizationCardProps['OrganizationInfo'] & {
    industry?: string;
    creationDate?: string;
    createdBy?: string;
    isPrimary?: boolean;
    source?: string;
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(data.organizationId);
    // You could add a toast notification here
  };

  return (
    <Card>
      <CardHeader>
        {/* <LogoContainer>
          {data.logoUrl ? (
            <Logo src={data.logoUrl} alt={`${data.name} logo`} />
          ) : (
            <NevadaSealPlaceholder />
          )}
        </LogoContainer> */}
        <Avatar name={data.name} imageUrl={data?.logoUrl} size={80} border="3px solid #F9FAFB" />
        
        <HeaderContent>
          <TitleRow>
            <OrganizationName>{data.name}</OrganizationName>
            <IdBadge onClick={handleCopyId} title="Copy Organization ID">
              {data.organizationId}
              <NewCopyIcon />
            </IdBadge>
            {data.isPrimary && <PrimaryBadge>Primary</PrimaryBadge>}
          </TitleRow>
          
          <SourceBadge>
            <InfoIcon />
            Source: {data.source ?? 'N/A'}
          </SourceBadge>
        </HeaderContent>
      </CardHeader>
      
      <DetailsSection>
        <DetailRow>
          <DetailLabel>Email Address:</DetailLabel>
          <DetailValue>{data.email}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Website:</DetailLabel>
          <DetailValue>{data.website}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Industry:</DetailLabel>
          <DetailValue>{data.industry ?? 'N/A'}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Creation Date:</DetailLabel>
          <DetailValue>{data.creationDate ?? 'N/A'}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Created By:</DetailLabel>
          <DetailValue>{data.createdBy ?? 'N/A'}</DetailValue>
        </DetailRow>
      </DetailsSection>
      
      <ViewDetailsButton onClick={onViewDetails}>
        <ViewIcon color={'var(--app-secondary-color)'}/>
        View Organization Details
      </ViewDetailsButton>
    </Card>
  );
};